/**
 * Parse objections text into structured objection/rebuttal pairs
 */
export function parseObjections(objections: string) {
  if (!objections || objections.length < 50) return [];

  // First, sanitize the input text
  const sanitizedText = objections
    .replace(/\\n/g, '\n')  // Handle escaped newlines
    .replace(/\n{3,}/g, '\n\n')  // Normalize multiple newlines
    .trim();
  
  // Check for duplicated sections by looking for repeating patterns
  let dedupedText = sanitizedText;
  
  // Identify duplicate section markers (e.g., "Objection 1:" appearing multiple times)
  const objectionMarkers = sanitizedText.match(/Objection\s+\d+:/gi) || [];
  const uniqueMarkers = [...new Set(objectionMarkers)];
  
  if (objectionMarkers.length > uniqueMarkers.length) {
    console.log("Detected duplicate objection sections, attempting to clean up");
    
    // Look for the first complete set of objections and responses
    const firstFullSetEnd = findEndOfFirstCompleteSet(sanitizedText);
    if (firstFullSetEnd > 0) {
      dedupedText = sanitizedText.substring(0, firstFullSetEnd);
      console.log("Trimmed duplicate content");
    }
  }
  
  // Parse the objections using multiple strategies
  const result = parseWithStrategy(dedupedText);
  
  // Ensure output is properly structured and deduplicated
  return deduplicateObjections(result);
}

/**
 * Find the end index of the first complete set of objections and responses
 */
function findEndOfFirstCompleteSet(text: string): number {
  // First, check if we have a pattern of "Objection X" followed by "Response"
  const matches = Array.from(text.matchAll(/Objection\s+(\d+):/gi));
  
  if (matches.length >= 2) {
    // Check for repeating numbers
    for (let i = 1; i < matches.length; i++) {
      const currentMatch = matches[i];
      const currentNum = parseInt(currentMatch[1]);
      const previousNum = parseInt(matches[i-1][1]);
      
      // If we see a number that's less than or equal to the previous one,
      // we've likely found the start of a duplicate section
      if (currentNum <= previousNum && currentMatch.index) {
        return currentMatch.index;
      }
    }
  }
  
  // Also check for repeating section headers
  const sectionMatches = Array.from(text.matchAll(/(?:Final Encouragement|Call to Faithfulness):/gi));
  if (sectionMatches.length > 1 && sectionMatches[1].index) {
    return sectionMatches[1].index;
  }
  
  return -1;
}

/**
 * Attempt to parse objections using multiple strategies
 */
function parseWithStrategy(text: string): { objection: string; rebuttal: string }[] {
  // Strategy 1: Look for "Objection X:" followed by "Response:"
  const strategy1Result = parseObjectionResponsePattern(text);
  if (strategy1Result.length > 0) {
    return strategy1Result;
  }
  
  // Strategy 2: Look for "Objection X:" followed by text and then "Objection Y:"
  const strategy2Result = parseObjectionToObjectionPattern(text);
  if (strategy2Result.length > 0) {
    return strategy2Result;
  }
  
  // Strategy 3: Look for numbered points that alternate between objections and responses
  const strategy3Result = parseNumberedAlternatingPattern(text);
  if (strategy3Result.length > 0) {
    return strategy3Result;
  }
  
  // Strategy 4: Parse paragraphs, assuming alternating objection/response
  return parseParagraphsAsAlternating(text);
}

/**
 * Strategy 1: Parse text with clear "Objection X:" and "Response:" markers
 */
function parseObjectionResponsePattern(text: string): { objection: string; rebuttal: string }[] {
  const result: { objection: string; rebuttal: string }[] = [];
  const pattern = /(?:Objection\s*(?:\d+)?:?\s*)(.*?)(?:(?:Response:?|Rebuttal:?)\s*)(.*?)(?=Objection\s*\d*:|$)/gis;
  
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match[1] && match[2]) {
      const objection = cleanText(match[1]);
      const rebuttal = cleanText(match[2]);
      
      if (objection && rebuttal) {
        result.push({ objection, rebuttal });
      }
    }
  }
  
  return result;
}

/**
 * Strategy 2: Parse text where objections are separated by the next objection
 */
function parseObjectionToObjectionPattern(text: string): { objection: string; rebuttal: string }[] {
  const result: { objection: string; rebuttal: string }[] = [];
  const parts = text.split(/(?=Objection\s*\d+:)/i);
  
  // Need at least 2 objections to use this pattern
  if (parts.length < 2) return [];
  
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (!part.match(/^Objection\s*\d+:/i)) continue;
    
    // Extract the objection title
    const titleMatch = part.match(/^Objection\s*\d+:(.*?)(?:\n|$)/i);
    if (!titleMatch) continue;
    
    const objectionTitle = cleanText(titleMatch[1]);
    
    // Everything after the title until the end is potentially the objection and response
    const content = part.substring(titleMatch[0].length).trim();
    
    // Try to split at "Response:" if it exists
    const contentParts = content.split(/Response:|Rebuttal:/i);
    
    if (contentParts.length >= 2) {
      // If we found "Response:", use that as the split
      const rebuttal = cleanText(contentParts.slice(1).join(' '));
      result.push({ objection: objectionTitle, rebuttal });
    } else {
      // Otherwise, assume the first half is objection, second half is response
      const midpoint = Math.floor(content.length / 2);
      const objectionContent = content.substring(0, midpoint);
      const rebuttalContent = content.substring(midpoint);
      
      result.push({ 
        objection: objectionTitle + " " + cleanText(objectionContent),
        rebuttal: cleanText(rebuttalContent)
      });
    }
  }
  
  return result;
}

/**
 * Strategy 3: Parse text with numbered points that alternate
 */
function parseNumberedAlternatingPattern(text: string): { objection: string; rebuttal: string }[] {
  const result: { objection: string; rebuttal: string }[] = [];
  const parts = text.split(/\d+\.\s/);
  
  // Skip the first part if it's empty or doesn't look like content
  const startIndex = parts[0].trim().length < 10 ? 1 : 0;
  
  // Need enough parts to make at least one objection-rebuttal pair
  if (parts.length - startIndex < 2) return [];
  
  for (let i = startIndex; i < parts.length - 1; i += 2) {
    if (i + 1 < parts.length) {
      const objection = cleanText(parts[i]);
      const rebuttal = cleanText(parts[i + 1]);
      
      if (objection && rebuttal) {
        result.push({ objection, rebuttal });
      }
    }
  }
  
  return result;
}

/**
 * Strategy 4: Parse paragraphs, assuming they alternate between objection and response
 */
function parseParagraphsAsAlternating(text: string): { objection: string; rebuttal: string }[] {
  const result: { objection: string; rebuttal: string }[] = [];
  const paragraphs = text.split(/\n\s*\n/);
  
  // Need at least 2 paragraphs
  if (paragraphs.length < 2) return [];
  
  // Determine if first paragraph looks like an objection
  const firstIsObjection = paragraphs[0].includes('?') || 
                          /atheist|skeptic|critic|object|challenge/i.test(paragraphs[0]);
  
  const startIndex = firstIsObjection ? 0 : 1;
  
  for (let i = startIndex; i < paragraphs.length - 1; i += 2) {
    if (i + 1 < paragraphs.length) {
      const objection = cleanText(paragraphs[i]);
      const rebuttal = cleanText(paragraphs[i + 1]);
      
      if (objection && rebuttal) {
        result.push({ objection, rebuttal });
      }
    }
  }
  
  return result;
}

/**
 * Clean and normalize text content
 */
function cleanText(text: string): string {
  if (!text) return '';
  
  return text
    .replace(/^\s*\*\*\s*/, '')  // Remove leading **
    .replace(/\s*\*\*\s*$/, '')  // Remove trailing **
    .replace(/^\d+\.\s*/, '')    // Remove leading number with dot
    .replace(/^[•-]\s*/, '')     // Remove bullet points
    .replace(/Response\s+\d+:\s*/i, '') // Remove "Response X:" prefix
    .trim();
}

/**
 * Remove duplicate objections by comparing normalized content
 */
function deduplicateObjections(objections: { objection: string; rebuttal: string }[]): { objection: string; rebuttal: string }[] {
  const uniqueObjections: { objection: string; rebuttal: string }[] = [];
  const seenObjections = new Set<string>();
  
  for (const item of objections) {
    // Skip obviously invalid items
    if (!item.objection || !item.rebuttal) continue;
    
    // Create normalized versions for comparison
    const normalizedObj = item.objection.toLowerCase().replace(/\s+/g, ' ');
    
    if (!seenObjections.has(normalizedObj)) {
      seenObjections.add(normalizedObj);
      uniqueObjections.push(item);
    }
  }
  
  return uniqueObjections;
}

/**
 * Clean hashtags and other special formatting from content
 */
export function cleanHashtags(content: string): string {
  if (!content) return '';
  
  return content
    .replace(/#[a-zA-Z0-9_]+/g, '') // Remove hashtags
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown
    .replace(/\*(.*?)\*/g, '$1') // Remove italic markdown
    .replace(/\n{3,}/g, '\n\n') // Normalize multiple line breaks
    .replace(/^\s*\d+\.\s*/gm, '') // Remove leading numbers at start of lines
    .replace(/Response\s+\d+:\s*/ig, '') // Remove "Response X:" prefixes
    .trim();
}
